import { Category, Shops } from '@infrab4a/connect';

export const BRAND_CATEGORY_DEFAULT_SHOPS: string[] = [
  Shops.GLAMSHOP,
  Shops.MENSMARKET
];

export function getShopCategoryDescriptionSaveValue(
  html: string | null | undefined,
  plainText: string | null | undefined
): string | null {
  if (html?.length && plainText?.trim()) {
    return html;
  }

  return null;
}

export function getShopCategoryPublishedSaveValue(
  published: boolean | null | undefined,
  isCreate: boolean
): boolean {
  return isCreate ? false : !!published;
}

export function getShopCategoryShopsSaveValue(
  selectedShops: string[] | null | undefined
): string[] {
  return (selectedShops ?? []).filter((shop) => !!shop);
}

export function getShopCategoryShopsForTypeChange(
  type: string,
  currentShops: string[] | null | undefined,
  overrideShops?: string[] | null
): string[] {
  if (overrideShops != null) {
    return getShopCategoryShopsSaveValue(overrideShops);
  }

  const current = getShopCategoryShopsSaveValue(currentShops);
  if (current.length) {
    return current;
  }

  if (type === 'brandCategory') {
    return [...BRAND_CATEGORY_DEFAULT_SHOPS];
  }

  return current;
}

export function isShopCategoryInfoSaveDisabled(
  invalid: boolean,
  pristine: boolean,
  hasDescriptionChange = false
): boolean {
  return invalid || (pristine && !hasDescriptionChange);
}

export function normalizeShopCategoryDescriptionHtml(
  html: string | null | undefined
): string {
  const value = html ?? '';
  const withoutEmptyMarkup = value
    .replace(/<p><br><\/p>/gi, '')
    .replace(/<p><\/p>/gi, '')
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();

  return withoutEmptyMarkup ? value.trim() : '';
}

export function hasShopCategoryDescriptionChanged(
  currentHtml: string | null | undefined,
  savedHtml: string | null | undefined
): boolean {
  return (
    normalizeShopCategoryDescriptionHtml(currentHtml) !==
    normalizeShopCategoryDescriptionHtml(savedHtml)
  );
}

export function shouldCopyShopCategoryMetadata(
  metadatas:
    | Array<{ title?: string | null; description?: string | null }>
    | null
    | undefined
): boolean {
  if (!metadatas?.length) {
    return true;
  }

  const titles = new Set(metadatas.map((item) => item.title ?? ''));
  const descriptions = new Set(
    metadatas.map((item) => item.description ?? '')
  );
  return titles.size === 1 && descriptions.size === 1;
}

type ShopCategoryConnectListAction<T> = {
  action?: string;
  value?: T;
};

export function getShopCategorySentFilters(
  sentFilters:
    | Category['filters']
    | ShopCategoryConnectListAction<Category['filters']>
    | null
    | undefined
): Category['filters'] | undefined {
  if (Array.isArray(sentFilters)) {
    return sentFilters;
  }

  if (!sentFilters || typeof sentFilters !== 'object') {
    return undefined;
  }

  if (sentFilters.action === 'remove') {
    return [];
  }

  if (Array.isArray(sentFilters.value)) {
    return sentFilters.value;
  }

  return undefined;
}

type ShopCategorySavePayload = Omit<
  Partial<Category>,
  'filters' | 'parent' | 'parentId'
> & {
  filters?:
    | Category['filters']
    | ShopCategoryConnectListAction<Category['filters']>;
  parent?: Category['parent'] | { action?: string } | null;
  parentId?: Category['parentId'] | { action?: string } | null;
};

export type ShopCategoryFormType =
  | 'collection'
  | 'levelOneCategory'
  | 'category'
  | 'brandCategory';

export function getShopCategoryFormType(
  category: Partial<Category> | null | undefined
): ShopCategoryFormType {
  if (category?.isCollection && category?.brandCategory) {
    return 'brandCategory';
  }

  if (category?.isCollection) {
    return 'collection';
  }

  const hasParent = !!category?.parent || !!category?.parentId;
  if (category?.reference && !category.isCollection && !hasParent) {
    return 'levelOneCategory';
  }

  return 'category';
}

function isConnectAction(
  value: unknown
): value is { action?: string; value?: unknown } {
  return !!value && typeof value === 'object' && 'action' in value;
}

export function getShopCategorySavedParent(
  sentParent: unknown
): Category['parent'] | null | undefined {
  if (sentParent == null) {
    return sentParent as null | undefined;
  }

  if (isConnectAction(sentParent)) {
    return sentParent.action === 'null' ? null : undefined;
  }

  return sentParent as Category['parent'];
}

export function getShopCategorySavedParentId(
  sentParentId: unknown
): Category['parentId'] | null | undefined {
  if (sentParentId == null) {
    return sentParentId as null | undefined;
  }

  if (isConnectAction(sentParentId)) {
    return sentParentId.action === 'null' ? null : undefined;
  }

  return sentParentId as Category['parentId'];
}

function pickShopCategoryParentAfterSave(
  updated: unknown,
  sent: unknown,
  current: Category['parent'] | null | undefined
): Category['parent'] | null | undefined {
  if (updated != null && !isConnectAction(updated)) {
    return updated as Category['parent'];
  }

  const saved = getShopCategorySavedParent(sent);
  return saved !== undefined ? saved : current;
}

function pickShopCategoryParentIdAfterSave(
  updated: unknown,
  sent: unknown,
  current: Category['parentId'] | null | undefined
): Category['parentId'] | null | undefined {
  if (updated != null && !isConnectAction(updated)) {
    return updated as Category['parentId'];
  }

  const saved = getShopCategorySavedParentId(sent);
  return saved !== undefined ? saved : current;
}

export function mergeShopCategoryAfterSave(
  current: Partial<Category> | null | undefined,
  updated: Partial<Category> | null | undefined,
  sent?: ShopCategorySavePayload | null
): Partial<Category> {
  return {
    ...(current ?? {}),
    ...(updated ?? {}),
    metadatas: updated?.metadatas ?? sent?.metadatas ?? current?.metadatas,
    shops: updated?.shops ?? sent?.shops ?? current?.shops,
    images: updated?.images ?? sent?.images ?? current?.images,
    filters:
      updated?.filters ??
      getShopCategorySentFilters(sent?.filters) ??
      current?.filters,
    conditions: updated?.conditions ?? sent?.conditions ?? current?.conditions,
    parent: pickShopCategoryParentAfterSave(
      updated?.parent,
      sent?.parent,
      current?.parent
    ),
    parentId: pickShopCategoryParentIdAfterSave(
      updated?.parentId,
      sent?.parentId,
      current?.parentId
    )
  };
}
